import { Currency } from '@core/constants/data-types.constants';
import { CurrencyAmount } from '@core/models/currency-amount.model';
import { Parser } from 'hot-formula-parser';
import { convert, CurrencyExchange } from './currency-exchange';
import { InsuranceTarget } from './insurance-target.model';
import { LifePlan } from './life-plan.model';
import { calculateTotalFee, calculateTotalInvestment, Product } from './product.model';
import { NULL_EXPR } from '@angular/compiler/src/output/output_ast';

export const COVERAGE_BASIC_ASSURED_AMOUNT_VAR = 'VAR_BASIC';

export interface ProductCoverage {
  id: string;
  name: string;
  description: string;
  percentageText: string;
  valueType: 'number' | 'percentage';
  type: 'included' | 'activable' | 'not-included';
  affectsOthers?: boolean;
  isAmountModifiable?: boolean;
  amountLimits?: ProductCoverageLimits;
  percentageLimits?: ProductCoverageLimits;
  condition?: ProductCoverageCondition;
  active?: boolean;
  valid?: boolean;
  currentAmount?: CurrencyAmount;
  currentFee?: CurrencyAmount;
  amountText?: string;
  group?: string;
}

export interface ProductCoverageLimits {
  min: ProductCoverageLimit;
  max: ProductCoverageLimit;
}

export interface ProductCoverageLimit {
  currency: Currency;
  formula: string;
  amount?: number;
  percentage?: number;
}

export interface ProductCoverageCondition {
  formula: string;
}

export interface GroupCoverage {
  group: string;
  coverages: ProductCoverage[];
  selectedCoverage: ProductCoverage;
}

// WIP: New superación lite
export function calculateProductAvailability(product: Product, coverage: ProductCoverage, lifePlan: LifePlan) {
  if (!coverage.condition) {
    return;
  }
  const conditionParser = new Parser();
  conditionParser.setVariable(
    'HAS_SPOUSE',
    (lifePlan.spouse && lifePlan.spouse.personal && lifePlan.spouse.personal.birthDate) != null
  );
  const showCoverage = conditionParser.parse(coverage.condition.formula);

  if (showCoverage.result) {
    coverage.type = 'activable';
    coverage.isAmountModifiable = true;
  } else {
    coverage.active = false;
    coverage.type = 'not-included';
    coverage.isAmountModifiable = false;
  }
}

export function calculateCoverageLimits(
  product: Product,
  coverage: ProductCoverage,
  insuranceTarget: InsuranceTarget,
  currencyExchange: CurrencyExchange
) {
  const minParser = new Parser();
  const maxParser = new Parser();
  const minPercentageParser = new Parser();
  const maxPercentageParser = new Parser();
  const coverageAmountLimits = coverage.amountLimits;
  const coveragePercentageLimits = coverage.percentageLimits;
  if (coverageAmountLimits) {
    if (product.milestone) {
      const ageWhenTemporalityComplete = product.milestone.userAge - insuranceTarget.insured.age;
      minParser.setVariable('VAR_AGE_TEMPORALITY_COMPLEATED', ageWhenTemporalityComplete);
      maxParser.setVariable('VAR_AGE_TEMPORALITY_COMPLEATED', ageWhenTemporalityComplete);
      if (coveragePercentageLimits) {
        minPercentageParser.setVariable('VAR_AGE_TEMPORALITY_COMPLEATED', ageWhenTemporalityComplete);
        maxPercentageParser.setVariable('VAR_AGE_TEMPORALITY_COMPLEATED', ageWhenTemporalityComplete);
      }
    }
    switch (product.type) {
      case 'normal':
        setParserAmountVariable(
          minParser,
          'VAR_BASIC',
          product.currentAssuredAmount,
          coverageAmountLimits.min.currency,
          currencyExchange
        );
        setParserAmountVariable(
          maxParser,
          'VAR_BASIC',
          product.currentAssuredAmount,
          coverageAmountLimits.max.currency,
          currencyExchange
        );
        if (coveragePercentageLimits) {
          setParserAmountVariable(
            minPercentageParser,
            'VAR_BASIC',
            product.currentAssuredAmount,
            coveragePercentageLimits.min.currency,
            currencyExchange
          );
          setParserAmountVariable(
            maxPercentageParser,
            'VAR_BASIC',
            product.currentAssuredAmount,
            coveragePercentageLimits.max.currency,
            currencyExchange
          );
        }

        const totalCurrentFee: CurrencyAmount = {
          amount: calculateTotalFee(product),
          currency: product.selectedCurrency
        };
        if (totalCurrentFee) {
          setParserAmountVariable(
            minParser,
            'VAR_CURRENT_FEE',
            totalCurrentFee,
            coverageAmountLimits.min.currency,
            currencyExchange
          );
          setParserAmountVariable(
            maxParser,
            'VAR_CURRENT_FEE',
            totalCurrentFee,
            coverageAmountLimits.max.currency,
            currencyExchange
          );
          if (coveragePercentageLimits) {
            setParserAmountVariable(
              minPercentageParser,
              'VAR_CURRENT_FEE',
              totalCurrentFee,
              coveragePercentageLimits.min.currency,
              currencyExchange
            );
            setParserAmountVariable(
              maxPercentageParser,
              'VAR_CURRENT_FEE',
              totalCurrentFee,
              coveragePercentageLimits.max.currency,
              currencyExchange
            );
          }
        }
        break;
      case 'investment':
        setParserAmountVariable(
          minParser,
          'VAR_BASIC',
          product.amountAtEndOfTemporality,
          coverageAmountLimits.min.currency,
          currencyExchange
        );
        setParserAmountVariable(
          maxParser,
          'VAR_BASIC',
          product.amountAtEndOfTemporality,
          coverageAmountLimits.max.currency,
          currencyExchange
        );
        if (coveragePercentageLimits) {
          setParserAmountVariable(
            minPercentageParser,
            'VAR_BASIC',
            product.amountAtEndOfTemporality,
            coveragePercentageLimits.min.currency,
            currencyExchange
          );
          setParserAmountVariable(
            maxPercentageParser,
            'VAR_BASIC',
            product.amountAtEndOfTemporality,
            coveragePercentageLimits.max.currency,
            currencyExchange
          );
        }

        const totalInvestment: CurrencyAmount = {
          amount: calculateTotalInvestment(product, insuranceTarget.insured.age),
          currency: product.selectedCurrency
        };
        if (totalInvestment) {
          setParserAmountVariable(
            minParser,
            'VAR_TOTAL_INVESTMENT',
            totalInvestment,
            coverageAmountLimits.min.currency,
            currencyExchange
          );
          setParserAmountVariable(
            maxParser,
            'VAR_TOTAL_INVESTMENT',
            totalInvestment,
            coverageAmountLimits.max.currency,
            currencyExchange
          );
          if (coveragePercentageLimits) {
            setParserAmountVariable(
              minPercentageParser,
              'VAR_TOTAL_INVESTMENT',
              totalInvestment,
              coveragePercentageLimits.min.currency,
              currencyExchange
            );
            setParserAmountVariable(
              maxPercentageParser,
              'VAR_TOTAL_INVESTMENT',
              totalInvestment,
              coveragePercentageLimits.max.currency,
              currencyExchange
            );
          }
        }
        break;
    }
    product.coverages
      .filter(c => c.type === 'included')
      .forEach(c => {
        if (c.currentAmount) {
          setParserAmountVariable(
            minParser,
            `VAR_${c.id}`,
            c.currentAmount,
            coverageAmountLimits.min.currency,
            currencyExchange
          );
          setParserAmountVariable(
            maxParser,
            `VAR_${c.id}`,
            c.currentAmount,
            coverageAmountLimits.max.currency,
            currencyExchange
          );
          if (coveragePercentageLimits) {
            setParserAmountVariable(
              minPercentageParser,
              `VAR_${c.id}`,
              c.currentAmount,
              coveragePercentageLimits.min.currency,
              currencyExchange
            );
            setParserAmountVariable(
              maxPercentageParser,
              `VAR_${c.id}`,
              c.currentAmount,
              coveragePercentageLimits.max.currency,
              currencyExchange
            );
          }
        }
      });
    const minResult: { error?: string; result: number } = minParser.parse(coverageAmountLimits.min.formula);
    const maxResult: { error?: string; result: number } = maxParser.parse(coverageAmountLimits.max.formula);

    coverage.amountLimits!.min.amount = minResult.result;
    coverage.amountLimits!.max.amount = maxResult.result;

    if (coveragePercentageLimits) {
      const minPercentageResult: { error?: string; result: number } = minPercentageParser.parse(
        coveragePercentageLimits.min.formula
      );
      const maxPercentageResult: { error?: string; result: number } = maxPercentageParser.parse(
        coveragePercentageLimits.max.formula
      );

      coverage.percentageLimits!.min.percentage = minPercentageResult.result;
      coverage.percentageLimits!.max.percentage = maxPercentageResult.result;
    }
  }
}

function setParserAmountVariable(
  parser: any,
  variable: string,
  amount: CurrencyAmount,
  to: Currency,
  currencyExchange: CurrencyExchange
) {
  const amountConvertedForParser = convert(amount, to, currencyExchange);
  parser.setVariable(variable, amountConvertedForParser.amount);
}
